import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  ButtonProps,
} from '@mui/material'
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AmericanExpressIcon,
  DeliveryIcon,
  DineInIcon,
  DiscoverIcon,
  MasterCardIcon,
  PickupIcon,
  VisaIcon,
} from 'components/images'
import {
  useCurrentStore,
  useOrdering,
  useStoreBizHours,
  useEditOrderDialog,
  useAppSettings,
} from 'hooks'
import { TypeRoute } from 'core/TypeRoute'
import { OrderType } from 'models'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import MailIcon from '@mui/icons-material/Mail'
import PlaceIcon from '@mui/icons-material/Place'
import PhoneIcon from '@mui/icons-material/Phone'
import { KBreadcrumbs } from 'components/core/KBreadcrumbs'
import { StoreName } from 'components/ordering/StoreName'
import { OpenClosedSign } from 'components/ordering/OpenClosedSign'
import { Background2 } from 'components/backgrounds/Background2'
import { KCurrentTime } from 'components/core/KCurrentTime'
import { KFooter } from 'components/core/KFooter'
import { BizHoursSection } from 'components/ordering/BizHoursSection'

interface OrderButtonProps extends Pick<ButtonProps, 'onClick'> {
  icon: ReactNode
  text: string
  variant: 'square' | 'rectangle'
}

const OrderButton: FC<OrderButtonProps> = ({ icon, text, onClick }) => {
  const theme = useTheme()

  return (
    <Button
      onClick={onClick}
      sx={{
        flexDirection: 'column',
        '& svg': {
          width: 175,
          minWidth: 175,
        },
        '& h2': {
          marginTop: theme.spacing(2),
        },
        borderRadius: 4,
        border: `3px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.common.white,
        '&:hover': {
          backgroundColor: theme.palette.primary.light,
        },
        '&:active': {
          backgroundColor: theme.palette.primary.dark,
        },
        width: '28%',
        padding: `${theme.spacing(5)} ${theme.spacing(2)} ${theme.spacing(3)}`,
        [theme.breakpoints.down('md')]: {
          flexDirection: 'row',
          padding: theme.spacing(2),
          width: '100%',
          minHeight: 120,
          '& svg': {
            width: 64,
            minWidth: 64,
            height: 64,
          },
          '& h2': {
            marginTop: 0,
            marginLeft: 6,
          },
        },
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'row',
          padding: theme.spacing(2),
          borderRadius: 3,
          minHeight: 72,
          '& svg': {
            width: 40,
            minWidth: 40,
            height: 40,
          },
          '& h2': {
            marginTop: 0,
            marginLeft: 4,
          },
        },
      }}
    >
      {icon}
      <Typography variant="h2">{text}</Typography>
    </Button>
  )
}

export const StoreInfoPage = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()

  const { setHome } = useAppSettings()
  const [query] = useSearchParams()
  useEffect(() => {
    setHome(query.get('home') ?? '')
  }, [setHome, query])

  const { showEditOrderDialog } = useEditOrderDialog()

  const { store } = useCurrentStore()
  const breadcrumbs = useMemo(
    () => [{ label: t('Stores') }, { label: store?.name ?? '' }],
    [store, t],
  )
  const { isOpening, bizHour } = useStoreBizHours()
  const { createOrder } = useOrdering()

  const _createOrder = useCallback(
    (orderType: OrderType) => {
      if (store) {
        createOrder({ orderType })
        showEditOrderDialog()
        navigate(generatePath(TypeRoute.Ordering, { storeId: store.id }))
      }
    },
    [store, createOrder, navigate, showEditOrderDialog],
  )

  useEffect(() => {
    if (store && store.info_page_settings?.enable_info_page === false) {
      _createOrder('delivery')
    }
  }, [store]) // eslint-disable-line react-hooks/exhaustive-deps

  // if info page is disabled, do not render anything
  //and wait for redirect to ordering page in useEffect above
  if (!store || store.info_page_settings?.enable_info_page === false) {
    return null
  }

  const isAcceptAll =
    store.accept_delivery && store.accept_dinein && store.accept_pickup

  const isDeliveryByThirdParty =
    store.delivery_settings?.delivery_type === 'third_party'

  const getDeliveryButtonText = () => {
    const defaultText = 'Start a Delivery order'

    let deliveryButtonText
    if (isDeliveryByThirdParty) {
      deliveryButtonText =
        store.delivery_settings?.third_party_delivery_button_text
    } else {
      deliveryButtonText = infoPageSettings?.delivery_button_text
    }

    return t(deliveryButtonText || defaultText)
  }

  const handleDeliveryClick = () => {
    if (isDeliveryByThirdParty) {
      window.open(
        store.delivery_settings?.third_party_delivery_button_link!,
        '_blank',
      )
    } else {
      _createOrder('delivery')
    }
  }

  const infoPageSettings = store.info_page_settings
  const enableTextLine = infoPageSettings?.enable_text_line === true

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack sx={{ flexGrow: 1, overflowY: 'auto', width: '100%' }}>
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Stack sx={{ paddingY: 1.5, gap: 1.5 }}>
            <KBreadcrumbs breadcrumbs={breadcrumbs} />
            {infoPageSettings?.show_store_logo === true && store.image_url && (
              <Box
                component="img"
                alt="store logo"
                src={store.image_url}
                sx={{
                  display: 'none',
                  [theme.breakpoints.down('sm')]: {
                    display: 'block',
                    maxWidth: 120,
                  },
                }}
              />
            )}
            <StoreName store={store} />
            <OpenClosedSign isOpening={isOpening} />
            <BizHoursSection isOpening={isOpening} currentBizHour={bizHour} />
            {infoPageSettings?.enable_store_phone === true && store.phone && (
              <Stack direction="row" spacing={2} alignItems="center">
                <PhoneIcon color="primary" />
                <Typography variant="body2">
                  {store.phone.replace(/^(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
                </Typography>
              </Stack>
            )}
            {infoPageSettings?.enable_store_email === true &&
              infoPageSettings.store_email && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <MailIcon color="primary" />
                  <Typography variant="body2">
                    {infoPageSettings.store_email}
                  </Typography>
                </Stack>
              )}
            {infoPageSettings?.enable_store_address === true &&
              store.address && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <PlaceIcon color="primary" />
                  <Typography variant="body2">{store.address}</Typography>
                </Stack>
              )}
          </Stack>
          {infoPageSettings?.show_store_logo === true && store.image_url && (
            <Box
              component="img"
              alt="store logo"
              src={store.image_url}
              sx={{
                maxHeight: 200,
                [theme.breakpoints.down('md')]: { maxWidth: 140 },
                [theme.breakpoints.down('sm')]: { display: 'none' },
              }}
            />
          )}
        </Container>
        <Background2
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              paddingY: 7,
              textAlign: 'center',
              [theme.breakpoints.down('md')]: { py: 3 },
            }}
          >
            {enableTextLine && infoPageSettings?.intro_text_1 && (
              <Typography variant="h3" fontWeight={500}>
                {infoPageSettings.intro_text_1}
              </Typography>
            )}
            {enableTextLine && infoPageSettings?.intro_text_2 && (
              <Typography variant="h3" fontWeight={500}>
                {infoPageSettings.intro_text_2}
              </Typography>
            )}
            {store.active_promo_code?.instruction_line_1 && (
              <Typography variant="h3" fontWeight={500}>
                {store.active_promo_code.instruction_line_1}
              </Typography>
            )}
            <Stack
              sx={{
                justifyContent: isAcceptAll ? 'space-between' : 'space-around',
                flexDirection: 'row',
                marginTop: 4,
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                  gap: 2,
                },
                [theme.breakpoints.down('sm')]: {
                  flexDirection: 'column',
                  gap: 1.5,
                },
              }}
            >
              {store.accept_delivery && (
                <OrderButton
                  icon={<DeliveryIcon />}
                  text={getDeliveryButtonText()}
                  variant="square"
                  onClick={handleDeliveryClick}
                />
              )}
              {store.accept_dinein && (
                <OrderButton
                  icon={<DineInIcon />}
                  text={t(
                    infoPageSettings?.dinein_button_text ||
                      'Start a Dine-in order',
                  )}
                  variant="square"
                  onClick={() => _createOrder('dinein')}
                />
              )}
              {store.accept_pickup && (
                <OrderButton
                  icon={<PickupIcon />}
                  text={t(
                    infoPageSettings?.pickup_button_text ||
                      'Start a Pick-up order',
                  )}
                  variant="square"
                  onClick={() => _createOrder('pickup')}
                />
              )}
            </Stack>
            {infoPageSettings?.enable_payment_icon === true && (
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 9,
                  [theme.breakpoints.down('md')]: { mt: 4 },
                }}
              >
                <VisaIcon />
                <MasterCardIcon />
                <AmericanExpressIcon />
                <DiscoverIcon />
              </Stack>
            )}
            <KCurrentTime
              variant="body1"
              sx={{ textAlign: 'center', marginTop: 4 }}
            />
          </Container>
        </Background2>
      </Stack>
      <KFooter />
    </Stack>
  )
}
